import { atom } from 'jotai';
import type { SuggestionDecision } from '@/services/api';

/**
 * Decisions on optimization suggestions waiting to be sent, or sent in this
 * tab. They run one at a time, in the order they were queued, from a runner
 * mounted in the Layout, so they keep going while the trader moves around the
 * panel. Nothing here survives a reload: a suggestion that never started is
 * still `pending` on the server and can be queued again.
 */

export const QUEUE_JOB_STATUSES = ['queued', 'running', 'done', 'error'] as const;
export type QueueJobStatus = (typeof QUEUE_JOB_STATUSES)[number];

export interface QueueJob {
  id: string;
  executionId: string;
  reportDate: string;
  suggestionKey: string;
  decision: SuggestionDecision;
  /** Shown in the progress list and the toasts. */
  label: string;
  status: QueueJobStatus;
  /** Backend error code, e.g. SUGGESTION_STALE. */
  errorCode: string | null;
  message: string | null;
  /** The change reached DV360 but its Advia OS record failed. */
  recordFailed: boolean;
}

export type NewQueueJob = Pick<QueueJob, 'executionId' | 'reportDate' | 'suggestionKey' | 'decision' | 'label'>;

export const queueJobsAtom = atom<QueueJob[]>([]);

let nextJobId = 0;

function sameSuggestion(a: Pick<QueueJob, 'executionId' | 'reportDate' | 'suggestionKey'>, b: typeof a): boolean {
  return a.executionId === b.executionId && a.reportDate === b.reportDate && a.suggestionKey === b.suggestionKey;
}

export function isActive(job: QueueJob): boolean {
  return job.status === 'queued' || job.status === 'running';
}

/** Add decisions at the end of the queue; a suggestion already waiting or running is skipped. */
export const enqueueJobsAtom = atom(null, (get, set, jobs: NewQueueJob[]) => {
  const current = get(queueJobsAtom);
  const added: QueueJob[] = [];
  for (const job of jobs) {
    const waiting = [...current, ...added].some((other) => isActive(other) && sameSuggestion(other, job));
    if (waiting) continue;
    nextJobId += 1;
    added.push({
      ...job,
      id: `job-${nextJobId}`,
      status: 'queued',
      errorCode: null,
      message: null,
      recordFailed: false,
    });
  }
  if (added.length > 0) set(queueJobsAtom, [...current, ...added]);
});

export const updateJobAtom = atom(null, (get, set, update: Pick<QueueJob, 'id'> & Partial<QueueJob>) => {
  set(
    queueJobsAtom,
    get(queueJobsAtom).map((job) => (job.id === update.id ? { ...job, ...update } : job))
  );
});

/** Forget the finished jobs, keeping whatever is still waiting or running. */
export const clearFinishedJobsAtom = atom(null, (get, set) => {
  set(queueJobsAtom, get(queueJobsAtom).filter(isActive));
});
